import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";
import { client, useFetch } from "@/lib/api";
import { PageHero } from "@/components/blocks/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Contact = () => {
  const { data: ministry } = useFetch("/ministry");
  const contact = ministry?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await client.post("/contact", form);
      setDone(true);
      toast.success("Your message has been sent.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHero
        overline="Contact"
        title="Get in touch"
        subtitle="Reach out to Flames of Fire World Evangelism and Tabernacle of Grace Church."
        image="https://images.unsplash.com/photo-1497621122273-f5cfb6065c56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto grid max-w-6xl gap-16 px-5 lg:grid-cols-5 lg:px-8">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold text-navy">We’d love to hear from you</h2>
            <p className="mt-3 text-muted-foreground">Send us a message and our team will respond.</p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4 border border-border bg-secondary/40 p-5">
                <Mail className="mt-0.5 h-5 w-5 text-brand-orange" />
                <div>
                  <p className="font-display text-sm font-semibold text-navy">Email</p>
                  {contact.email ? (
                    <a href={`mailto:${contact.email}`} className="text-sm text-muted-foreground hover:text-brand-orange">{contact.email}</a>
                  ) : <p className="text-sm italic text-muted-foreground/70">Contact email coming soon</p>}
                </div>
              </div>
              <div className="flex items-start gap-4 border border-border bg-secondary/40 p-5">
                <Phone className="mt-0.5 h-5 w-5 text-brand-orange" />
                <div>
                  <p className="font-display text-sm font-semibold text-navy">Phone</p>
                  {contact.phone ? (
                    <a href={`tel:${contact.phone}`} className="text-sm text-muted-foreground hover:text-brand-orange">{contact.phone}</a>
                  ) : <p className="text-sm italic text-muted-foreground/70">Phone coming soon</p>}
                </div>
              </div>
              <div className="flex items-start gap-4 border border-border bg-secondary/40 p-5">
                <MapPin className="mt-0.5 h-5 w-5 text-brand-orange" />
                <div>
                  <p className="font-display text-sm font-semibold text-navy">Location</p>
                  {contact.address ? (
                    <p className="text-sm text-muted-foreground">{contact.address}</p>
                  ) : <p className="text-sm italic text-muted-foreground/70">Address coming soon</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div data-testid="contact-success" className="border border-border bg-secondary/40 p-10 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-brand-orange" />
                <h3 className="mt-5 font-display text-2xl font-bold text-navy">Message sent</h3>
                <p className="mt-2 text-muted-foreground">Thank you for reaching out. We’ll be in touch.</p>
                <Button onClick={() => setDone(false)} className="mt-6 rounded-sm bg-navy uppercase tracking-wide hover:bg-navy-surface">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="cname">Name *</Label>
                    <Input id="cname" data-testid="contact-name" value={form.name} onChange={update("name")} className="mt-2 rounded-sm" placeholder="Your name" />
                  </div>
                  <div>
                    <Label htmlFor="cemail">Email *</Label>
                    <Input id="cemail" type="text" inputMode="email" data-testid="contact-email" value={form.email} onChange={update("email")} className="mt-2 rounded-sm" placeholder="you@email.com" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="csubject">Subject</Label>
                  <Input id="csubject" data-testid="contact-subject" value={form.subject} onChange={update("subject")} className="mt-2 rounded-sm" placeholder="How can we help?" />
                </div>
                <div>
                  <Label htmlFor="cphone">Phone (optional)</Label>
                  <Input id="cphone" data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="mt-2 rounded-sm" placeholder="Your phone number" />
                </div>
                <div>
                  <Label htmlFor="cmessage">Message *</Label>
                  <Textarea id="cmessage" data-testid="contact-message" value={form.message} onChange={update("message")} className="mt-2 min-h-[140px] rounded-sm" placeholder="Your message…" />
                </div>
                <Button type="submit" disabled={submitting} data-testid="contact-submit" className="w-full rounded-sm bg-brand-orange py-6 font-display uppercase tracking-wide hover:bg-brand-orange-600">
                  {submitting ? "Sending…" : (<><Send className="mr-2 h-4 w-4" /> Send Message</>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
